import characters from "../Characters.json";
import { migrateCollectionIds, normalizeCardId } from "./cardCatalog";
import {
    autoEquipAvatarFrameIfEmpty,
    autoEquipTitleIfEmpty,
    COSMETIC_TYPES,
    formatCosmeticUnlockMessages,
    normalizeCosmeticsState,
    syncCollectionAvatars,
    unlockAllGrantableCosmetics,
    unlockCosmetics,
} from "./cosmetics";
import { MAX_HEARTS, grantHearts, refillHeartsToMax } from "./heartEconomy";
import { grantNoAdsEntitlement } from "./iapEntitlements";
import {
    applyUnlockAllStoryLevels,
    createEmptyStoryProgress,
} from "./storyProgress";
import { clampTreats, formatTreats, grantTreats } from "./treats";

const CHARACTER_NAME_BY_ID = new Map(
    (Array.isArray(characters) ? characters : []).map((card) => [
        normalizeCardId(card?.id),
        String(card?.name || "").trim(),
    ])
);

function sanitizeCardIds(rawIds = []) {
    if (!Array.isArray(rawIds)) {
        return [];
    }
    return migrateCollectionIds(
        rawIds
            .map((raw) => Math.floor(Number(raw)))
            .filter((id) => Number.isFinite(id) && id > 0)
    );
}

function formatCardUnlockMessages(cardIds = []) {
    return cardIds.map((id) => {
        const name = CHARACTER_NAME_BY_ID.get(normalizeCardId(id));
        return name ? `Unlocked ${name}` : `Unlocked card #${id}`;
    });
}

export const REDEMPTION_STATE_VERSION = 1;

export function createEmptyRedemptionState() {
    return {
        version: REDEMPTION_STATE_VERSION,
        redeemedIds: [],
        redeemedAt: {},
    };
}

function uniqueIds(ids = []) {
    return Array.from(
        new Set(
            (Array.isArray(ids) ? ids : [])
                .map((id) => String(id || "").trim())
                .filter(Boolean)
        )
    ).sort();
}

export function normalizeRedemptionCodeInput(value = "") {
    return String(value || "")
        .toUpperCase()
        .replace(/[^A-Z0-9]+/g, "");
}

export function normalizeRedemptionState(raw = null) {
    const empty = createEmptyRedemptionState();
    if (!raw || typeof raw !== "object") {
        return empty;
    }

    const redeemedIds = uniqueIds(raw.redeemedIds);
    const redeemedAtRaw =
        raw.redeemedAt && typeof raw.redeemedAt === "object" ? raw.redeemedAt : {};
    const redeemedAt = {};

    redeemedIds.forEach((id) => {
        const timestamp = Number(redeemedAtRaw[id]);
        redeemedAt[id] = Number.isFinite(timestamp) && timestamp > 0 ? timestamp : 0;
    });

    return {
        version: REDEMPTION_STATE_VERSION,
        redeemedIds,
        redeemedAt,
    };
}

export function mergeRedemptionStates(localState = null, remoteState = null) {
    const local = normalizeRedemptionState(localState);
    const remote = normalizeRedemptionState(remoteState);
    const redeemedIds = uniqueIds([...local.redeemedIds, ...remote.redeemedIds]);
    const redeemedAt = {};

    redeemedIds.forEach((id) => {
        redeemedAt[id] = Math.max(local.redeemedAt[id] || 0, remote.redeemedAt[id] || 0);
    });

    return normalizeRedemptionState({
        redeemedIds,
        redeemedAt,
    });
}

export function markRedemptionClaimed(state = null, codeId, redeemedAtMs = Date.now()) {
    const normalized = normalizeRedemptionState(state);
    const id = String(codeId || "").trim();
    if (!id) {
        return normalized;
    }

    return normalizeRedemptionState({
        ...normalized,
        redeemedIds: [...normalized.redeemedIds, id],
        redeemedAt: {
            ...normalized.redeemedAt,
            [id]: Math.max(0, Math.floor(Number(redeemedAtMs) || 0)),
        },
    });
}

export function hasRedeemedCode(state = null, codeId = "") {
    return normalizeRedemptionState(state).redeemedIds.includes(
        String(codeId || "").trim()
    );
}

export function applyRedemptionGrant(
    currentState = {},
    rewards = {},
    { now = Date.now() } = {}
) {
    const nextState = {};
    const rewardMessages = [];

    const treatAmount = clampTreats(rewards.treats, Number.MAX_SAFE_INTEGER);
    if (treatAmount > 0) {
        nextState.treats = grantTreats(currentState.treats, treatAmount);
        rewardMessages.push(`+${formatTreats(treatAmount)}`);
    }

    const refillHearts = Boolean(rewards.refillHearts);
    const heartAmount = Math.max(0, Math.floor(Number(rewards.hearts) || 0));
    if (refillHearts) {
        nextState.hearts = refillHeartsToMax(currentState.hearts);
        nextState.heartLastRefillAt = now;
        rewardMessages.push("Hearts refilled");
    } else if (heartAmount > 0) {
        nextState.hearts = grantHearts(currentState.hearts, heartAmount);
        nextState.heartLastRefillAt =
            currentState.hearts <= 0 || currentState.hearts >= MAX_HEARTS
                ? now
                : currentState.heartLastRefillAt;
        rewardMessages.push(`+${heartAmount} ${heartAmount === 1 ? "Heart" : "Hearts"}`);
    }

    if (rewards.removeAds) {
        nextState.iapEntitlements = grantNoAdsEntitlement(currentState.iapEntitlements);
        rewardMessages.push("No Ads unlocked");
    }

    if (rewards.unlockAllStoryLevels) {
        nextState.storyProgress = applyUnlockAllStoryLevels(
            currentState.storyProgress
        );
        rewardMessages.push("All Story levels unlocked");
    } else if (rewards.resetStoryLevels) {
        nextState.storyProgress = createEmptyStoryProgress();
        rewardMessages.push("Story Mode reset to Level 1");
    }

    const cosmeticIds = Array.isArray(rewards.cosmeticIds) ? rewards.cosmeticIds : [];
    let cosmeticsAfterGrant = normalizeCosmeticsState(currentState.cosmetics);
    let cosmeticGrant = { grantedItems: [] };
    let autoEquipped = false;

    if (rewards.unlockAllCosmetics) {
        cosmeticGrant = unlockAllGrantableCosmetics(cosmeticsAfterGrant);
        cosmeticsAfterGrant = cosmeticGrant.state;
        rewardMessages.push("All cosmetics unlocked");
    } else if (cosmeticIds.length > 0) {
        cosmeticGrant = unlockCosmetics(cosmeticsAfterGrant, cosmeticIds);
        cosmeticsAfterGrant = cosmeticGrant.state;
        rewardMessages.push(
            ...formatCosmeticUnlockMessages(cosmeticGrant.grantedItems)
        );
    }

    const preferredFrameId =
        cosmeticGrant.grantedItems.find(
            (item) => item && item.type === COSMETIC_TYPES.AVATAR_FRAME
        )?.id || null;
    const preferredTitleId =
        cosmeticGrant.grantedItems.find(
            (item) => item && item.type === COSMETIC_TYPES.TITLE
        )?.id || null;

    if (preferredFrameId) {
        const frameResult = autoEquipAvatarFrameIfEmpty(
            cosmeticsAfterGrant,
            preferredFrameId
        );
        cosmeticsAfterGrant = frameResult.state;
        autoEquipped = autoEquipped || frameResult.equipped;
    }
    if (preferredTitleId) {
        const titleResult = autoEquipTitleIfEmpty(
            cosmeticsAfterGrant,
            preferredTitleId
        );
        cosmeticsAfterGrant = titleResult.state;
        autoEquipped = autoEquipped || titleResult.equipped;
    }

    nextState.cosmetics = normalizeCosmeticsState(cosmeticsAfterGrant);

    const requestedCardIds = sanitizeCardIds(rewards.cardIds);
    const ownedIds = migrateCollectionIds(currentState.collectionUnlocked);
    const newlyGrantedCardIds = requestedCardIds.filter(
        (id) => !ownedIds.some((owned) => normalizeCardId(owned) === normalizeCardId(id))
    );
    if (newlyGrantedCardIds.length > 0) {
        const collectionUnlocked = migrateCollectionIds([
            ...ownedIds,
            ...newlyGrantedCardIds,
        ]);
        nextState.collectionUnlocked = collectionUnlocked;
        nextState.cosmetics = syncCollectionAvatars(
            nextState.cosmetics,
            collectionUnlocked,
            {
                preferredCardId:
                    currentState.profileImageId || newlyGrantedCardIds[0],
            }
        );
        rewardMessages.push(...formatCardUnlockMessages(newlyGrantedCardIds));
    } else if (requestedCardIds.length > 0) {
        rewardMessages.push(
            requestedCardIds.length === 1
                ? "Card already owned"
                : "Cards already owned"
        );
    }

    const packCount = Math.max(0, Math.floor(Number(rewards.packs) || 0));
    if (packCount > 0) {
        rewardMessages.push(`+${packCount} ${packCount === 1 ? "Corgi Pack" : "Corgi Packs"}`);
    }

    return {
        nextState,
        rewardMessages,
        packCount,
        cosmeticGrant,
        autoEquipped,
        unlockAllStoryLevels: Boolean(rewards.unlockAllStoryLevels),
        unlockAllCosmetics: Boolean(rewards.unlockAllCosmetics),
        resetStoryLevels: Boolean(rewards.resetStoryLevels),
    };
}
